import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import orderBy from 'lodash/orderBy';
import { useParams, useHistory } from 'react-router-dom';
import { PrimaryButton, DefaultButton } from 'office-ui-fabric-react/lib/Button';
import { DialogType } from 'office-ui-fabric-react/lib/Dialog';
import { approveAgreement, rejectAgreement } from '../../actions/agreements';
import { fetchContractActivity } from '../../actions/contracts';
import TextButton from '../../components/TextButton';
import AgreementForm from '../../components/AgreementForm';
import Module from '../../components/Module';
import ModuleHeader from '../../components/ModuleHeader';
import ModuleContent from '../../components/ModuleContent';
import SubModule from '../../components/SubModule';
import SubModuleHeader from '../../components/SubModuleHeader';
import SubModuleContent from '../../components/SubModuleContent';
import PageTitle from '../../components/PageTitle';
import Main from '../../components/Main';
import SavingIndicator from '../../components/SavingIndicator';
import ContractActionRequired from '../../components/ContractActionRequired';
import PauseTransactionsDialog from '../../components/PauseTransactionsDialog';
import styles from './ReviewAgreementEdits.style';

/**
 * This page is only for approving a royalty fee after editing an approved royalty fee
 */
const ReviewAgreementEdits = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const { contractAddress, agreementAddress } = useParams();
  const contract = useSelector(state => get(state, `entities.contracts.${contractAddress}`, {}));
  const agreement = useSelector(state => get(state, `entities.agreements.${agreementAddress}`, {}));
  const latestActivity = useSelector(state => {
    const activities = orderBy(
      Object.values(get(state, `entities.activity`, {})).filter(
        activity =>
          activity.resource === agreementAddress && activity.operation.includes('submitted'),
      ),
      ['timestamp'],
      ['desc'],
    );
    return activities.length ? activities[0] : null;
  });

  useEffect(() => {
    if (
      agreement.approvalStatus &&
      agreement.approvalStatus !== 'PendingApproval' &&
      !isSubmitting
    ) {
      history.push(`/contracts/${contractAddress}`);
    }
  }, [history, agreement.approvalStatus, contractAddress, isSubmitting]);

  useEffect(() => {
    dispatch(fetchContractActivity(contractAddress));
  }, [dispatch, contractAddress]);

  const handleCancel = async () => {
    history.push(`/contracts/${contractAddress}`);
  };

  const handleReject = async () => {
    setIsSubmitting(true);
    const response = await dispatch(rejectAgreement(contractAddress, agreementAddress));

    if (!response.error) {
      history.push(`/contracts/${contractAddress}`);
    } else {
      setIsSubmitting(false);
    }
  };

  const handleApprove = async () => {
    setIsSubmitting(true);
    const response = await dispatch(approveAgreement(contractAddress, agreementAddress));

    if (!response.error) {
      history.push(`/contracts/${contractAddress}`);
    } else {
      setIsSubmitting(false);
    }
  };

  const handleAction = () => {
    // check if this is a newly created agreement with no previous state
    if (!latestActivity.oldData) setIsDialogVisible(true);
    else {
      const criticalFields = ['effectiveDate', 'terminationDate', 'rate', 'products', 'filters'];
      const {
        detailedDiff: { added, deleted, updated },
      } = latestActivity;
      const updatedFields = Object.keys({
        ...updated,
        ...deleted,
        ...added,
      });
      const hasCriticalEdits = updatedFields.some(field => criticalFields.includes(field));
      if (hasCriticalEdits) {
        setIsDialogVisible(true);
      } else {
        handleApprove();
      }
    }
  };

  return (
    <>
      <PageTitle>Review and Approve Royalty Fee</PageTitle>

      <Main>
        <Module className={styles.agreementModule}>
          <ContractActionRequired
            address={contractAddress}
            agreementAddress={agreementAddress}
            hideCta
            className={styles.actionRequired}
          />

          {contract && contract?.name && contract.contractId && (
            <ModuleHeader variant="large">{`Contract ${contract.name} (#${contract.contractId})`}</ModuleHeader>
          )}

          <ModuleContent>
            <SubModule key={agreement.address} disableAccordian>
              <SubModuleHeader variant="large">{agreement?.name}</SubModuleHeader>
              <SubModuleContent>
                <AgreementForm
                  reviewMode
                  contractAddress={contractAddress}
                  address={agreement.address}
                />
              </SubModuleContent>
            </SubModule>
          </ModuleContent>
        </Module>

        <div className={styles.buttonContainer}>
          <div className={styles.cancelButtonContainer}>
            <TextButton
              className={styles.cancelButton}
              onClick={handleCancel}
              disabled={isSubmitting}
              noPadding
            >
              Cancel
            </TextButton>
          </div>

          <div className={styles.saveSubmitButtonContainer}>
            {!isSubmitting ? (
              <>
                <DefaultButton onClick={handleReject}>Reject</DefaultButton>

                <PrimaryButton onClick={handleAction} disabled={isSubmitting}>
                  Approve
                </PrimaryButton>
              </>
            ) : (
              <SavingIndicator className={styles.saving} />
            )}
          </div>
        </div>
        <PauseTransactionsDialog
          onDismiss={() => setIsDialogVisible(false)}
          onSubmit={handleApprove}
          isSubmitting={isSubmitting}
          name={agreement.name}
          hidden={!isDialogVisible}
          dialogContentProps={{
            type: DialogType.normal,
            title: 'Pause/Reprocess Transactions',
            closeButtonAriaLabel: 'Close',
          }}
        />
      </Main>
    </>
  );
};

export default ReviewAgreementEdits;
